import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit } from '@angular/core';

import {
  BackgroundColor,
  CmsImage,
  ContainerType,
  FlexboxContentContainer,
  FlexboxItem,
  FlexDirection,
  Image,
  ImageSize,
  JustifyContent,
  PaddingStyle,
  WhatViewport,
} from '@digital-platform/rs-cms/domain';
import { ViewportService } from '@digital-platform/rs-shared/services';
import {
  ConcatInlineStylesPipe,
  PictureComponent,
} from '@digital-platform/rs-shared/ui';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { DynamicContentWrapperComponent } from '../dynamic-content-wrapper/dynamic-content-wrapper.component';

interface ItemAngularConfig {
  mobileClass?: string;
  midsizeClass?: string;
  desktopClass?: string;
  mobileInlineStyle?: string;
  midsizeInlineStyle?: string;
  desktopInlineStyle?: string;
  flexWrapClass?: string;
}

type FlexboxItemExtended = FlexboxItem & { angularConfig?: ItemAngularConfig };

interface ContainerAngularConfig {
  class?: string;
  mobileClass?: string;
  midsizeClass?: string;
  desktopClass?: string;
  style?: string;
  mobileStyle?: string;
  midsizeStyle?: string;
  desktopStyle?: string;
  isCarousel?: boolean;
  flexboxDisplay?: boolean;
  itemsWrapperClass?: string;
  childWrapperClass?: string;
  childWrapperInlineStyle?: string;
  mobileChildWrapperInlineStyle?: string;
  midsizeChildWrapperInlineStyle?: string;
  desktopChildWrapperInlineStyle?: string;
  containerClass?: string;
  mobileContainerClass?: string;
  midsizeContainerClass?: string;
  desktopContainerClass?: string;
}

@Component({
  selector: 'digital-platform-flexbox-content-container',
  standalone: true,
  imports: [CommonModule, DynamicContentWrapperComponent, PictureComponent],
  providers: [ConcatInlineStylesPipe],
  templateUrl: './flexbox-content-container.component.html',
  styleUrls: ['./flexbox-content-container.component.scss'],
})
export class FlexboxContentContainerComponent
  extends BasePTypeComponent
  implements FlexboxContentContainer, OnChanges, OnInit
{
  public override type = 'flexboxContentContainer' as const;

  public viewportService = inject(ViewportService);
  public concatInlineStyles = inject(ConcatInlineStylesPipe);

  @Input() bgColor: BackgroundColor | null = null;
  @Input() bottomBorder = false;
  @Input() leftBorder = false;
  @Input() rightBorder = false;
  @Input() topBorder = false;
  @Input() containerType: ContainerType | null = null;
  @Input() flexCenter = false;
  @Input() flexDirection: FlexDirection | null = null;
  @Input() flexVcenter = false;
  @Input() fullWidthBackground = false;
  @Input() height: number | null = 0;
  @Input() image: Image[] = [];
  @Input() justifyContent: JustifyContent | null = null;
  @Input() nestedFlexboxItem: FlexboxItem[] = [];
  @Input() imageSize: ImageSize | null = null;

  @Input() paddingBottom: PaddingStyle | null = null;
  @Input() paddingBottomDesktop: PaddingStyle | null = null;
  @Input() paddingBottomMidrange: PaddingStyle | null = null;
  @Input() paddingTop: PaddingStyle | null = null;
  @Input() paddingTopDesktop: PaddingStyle | null = null;
  @Input() paddingTopMidrange: PaddingStyle | null = null;
  @Input() paddingVertical: PaddingStyle | null = null;
  @Input() paddingVerticalDesktop: PaddingStyle | null = null;
  @Input() paddingVerticalMidrange: PaddingStyle | null = null;

  @Input() roundedCorner: string | null = null;
  @Input() webpMedia: Array<CmsImage> | null = null;
  @Input() constrainContentWidth: boolean | null = null;
  @Input() angularConfig: ContainerAngularConfig = {};

  public containerPaddingClasses = '';
  public cmsImage: CmsImage | null = null;
  public backgroundClasses = '';
  public containerClasses = '';
  public containerStyles = '';
  public childWrapperClasses = '';
  public childWrapperInlineStyle = '';

  ngOnInit() {
    this.viewportService.whatViewport$.subscribe(() => this.initClasses());
  }

  ngOnChanges() {
    this.initClasses();
  }

  private initClasses() {
    this.initContainerPaddingClasses(this.viewportService.whatViewport);
    this.initCmsImage(this.viewportService.whatViewport);
    this.initBackgroundClasses();
    this.initContainerClasses();
    this.initChildWrapperClasses();
    this.initChildWrapperInlineStyle();
  }

  public get isRowContainerType(): boolean {
    return this.containerType?.containerClass === 'row';
  }

  public initCmsImage({ mobile, midsize }: WhatViewport) {
    if (mobile) {
      this.cmsImage =
        this.image?.[0]?.webpMedia ||
        this.image?.[0]?.webpMediaMid ||
        this.image?.[0]?.webpMediaDesktop ||
        null;
    } else if (midsize) {
      this.cmsImage =
        this.image?.[0]?.webpMediaMid ||
        this.image?.[0]?.webpMedia ||
        this.image?.[0]?.webpMediaDesktop ||
        null;
    } else {
      this.cmsImage =
        this.image?.[0]?.webpMediaDesktop ||
        this.image?.[0]?.webpMediaMid ||
        this.image?.[0]?.webpMedia ||
        null;
    }
  }

  public getInnerInlineStyles(item: FlexboxItemExtended): string {
    const { isDesktop, isMobile, isMidsize } = this.viewportService;
    return this.concatInlineStyles.transform([
      isDesktop && item?.angularConfig?.desktopInlineStyle,
      isMobile && item?.angularConfig?.mobileInlineStyle,
      isMidsize && item?.angularConfig?.midsizeInlineStyle,
    ]);
  }

  public getInnerClasses(item: FlexboxItemExtended): string {
    const temp = [];
    const prefixClass = 'flexbox-items';

    temp.push(
      prefixClass,
      item.bgColor?.cssColorName,
      item.paddingTop?.paddingClass,
      item.paddingBottom?.paddingClass,
      item.paddingRight?.paddingClass,
      item.paddingLeft?.paddingClass,
      item.angularConfig?.flexWrapClass
    );

    // TODO: Fix the class that CMS is exposing for mobile. It exposes order-sm-<...> but it should be order-<...> to be mobile-first
    if (item.flexboxItemMobileOrder?.orderClass) {
      temp.push(
        item.flexboxItemMobileOrder.orderClass.replace(
          /order-sm-(\d)/g,
          'order-$1'
        )
      );
    }

    temp.push(item.flexboxItemTabletOrder, item.flexboxItemDesktopOrder);

    if (item.angularConfig && typeof item.angularConfig === 'string') {
      try {
        item.angularConfig = JSON.parse(item.angularConfig);
      } catch (e) {
        console.error(`Unable to parse ${item.angularConfig} as JSON`);
        item.angularConfig = {};
      }
    }

    if (item.angularConfig) {
      const { desktopClass, mobileClass, midsizeClass } = item.angularConfig;
      if (desktopClass && this.viewportService.isDesktop) {
        temp.push(desktopClass);
      } else if (midsizeClass && this.viewportService.isMidsize) {
        temp.push(midsizeClass);
      } else if (mobileClass && this.viewportService.isMobile) {
        temp.push(mobileClass);
      }
    }

    return this.parseInnerClasses(temp);
  }

  public parseInnerClasses(classArr: any[]): string {
    return classArr
      .map((data) => {
        if (typeof data === 'object' && data !== null) {
          return data.orderClass || '';
        }
        return data || '';
      })
      .join(' ')
      .replace(/,|\s+/g, ' ')
      .trim();
  }

  public initContainerPaddingClasses({
    mobile,
    midsize,
    desktop,
  }: WhatViewport) {
    const retVal = [];

    retVal.push(
      mobile && this.paddingVertical?.paddingClass,
      mobile && this.paddingTop?.paddingClass,
      mobile && this.paddingBottom?.paddingClass,
      midsize && this.paddingVerticalMidrange?.paddingClass,
      midsize && this.paddingTopMidrange?.paddingClass,
      midsize && this.paddingBottomMidrange?.paddingClass,
      desktop && this.paddingVerticalDesktop?.paddingClass,
      desktop && this.paddingTopDesktop?.paddingClass,
      desktop && this.paddingBottomDesktop?.paddingClass,
      this.constrainContentWidth && 'container'
    );

    if (this.angularConfig) {
      const {
        containerClass,
        mobileContainerClass,
        midsizeContainerClass,
        desktopContainerClass,
      } = this.angularConfig;
      retVal.push(
        containerClass,
        mobile && mobileContainerClass,
        midsize && midsizeContainerClass,
        desktop && desktopContainerClass
      );
    }

    this.containerPaddingClasses = retVal
      .filter((c) => c)
      .join(' ')
      .replace(/,|\s+/g, ' ');
  }

  public concatChildClasses(item: FlexboxItem): string {
    const concatClasses = [
      item.fbiMobile?.widthClass,
      item.fbiTablet?.widthClass,
      item.fbiDesktop?.widthClass,
      item.flexboxItemMobileOrder?.orderClass,
      item.flexboxItemTabletOrder?.orderClass,
      item.flexboxItemDesktopOrder?.orderClass,
      item.bgColor?.cssColorName,
      item.paddingTop?.paddingClass,
      item.paddingBottom?.paddingClass,
      item.paddingRight?.paddingClass,
      item.paddingLeft?.paddingClass,
      item.visibility?.visibilityClass,
      item.justifyContent?.justificationClass,
    ];

    // TODO: Fix the class that CMS is exposing for mobile. It exposes col-sm-<...> but it should be col-<...> to be mobile-first
    if (item.fbiMobile?.widthClass) {
      concatClasses.push(
        item.fbiMobile.widthClass.replace(/col-sm-(\d{1,2})/g, 'col-$1')
      );
    }

    if (item.mobileCenter100) {
      concatClasses.push('flexbox-items-mobile-100-center');
    }
    if (item.bottomBorder) {
      concatClasses.push('border-bottom');
    }
    if (item.topBorder) {
      concatClasses.push('border-top');
    }
    if (item.leftBorder) {
      concatClasses.push('border-left');
    }
    if (item.rightBorder) {
      concatClasses.push('border-right');
    }

    return concatClasses.join(' ').replace(/,/g, ' ');
  }

  private initContainerClasses() {
    let styles = '';
    const cls: Array<string | null | false | undefined> =
      this.initContainerBaseClasses;

    if (this.angularConfig) {
      const {
        class: c,
        midsizeClass,
        mobileClass,
        desktopClass,
        style,
        mobileStyle,
        midsizeStyle,
        desktopStyle,
      } = this.angularConfig;
      const { isMobile, isMidsize, isDesktop } = this.viewportService;
      if (style) styles += ' ' + style;
      cls.push(
        c,
        isMobile && mobileClass,
        isMidsize && midsizeClass,
        isDesktop && desktopClass
      );
      if (isMobile && mobileStyle) {
        styles += ' ' + mobileStyle;
      } else if (isMidsize && midsizeStyle) {
        styles += ' ' + midsizeStyle;
      } else if (isDesktop && desktopStyle) {
        styles += ' ' + desktopStyle;
      }
    }

    this.containerClasses = cls.filter((c) => c).join(' ');
    this.containerStyles = styles;
  }

  private get initContainerBaseClasses(): Array<
    string | null | false | undefined
  > {
    return [
      this.bgColor?.cssColorName,
      this.containerType?.containerClass,
      this.bottomBorder && 'border-bottom',
      this.leftBorder && 'border-left',
      this.rightBorder && 'border-right',
      this.topBorder && 'border-top',
      this.initContainerBaseClassesRoundCorner,
      ...this.initContainerBaseClassesFullBackground,
    ];
  }

  private get initContainerBaseClassesFullBackground() {
    if (!this.fullWidthBackground) {
      return [
        'main-block-flex',
        this.cmsImage && `resp-img-${this.revisionId}`,
      ];
    }
    return [];
  }

  private get initContainerBaseClassesRoundCorner(): string {
    if (this.roundedCorner) {
      return Array.isArray(this.roundedCorner)
        ? this.roundedCorner.map((c) => `rounded-${c}`).join(' ')
        : `rounded-${this.roundedCorner}`;
    }
    return '';
  }

  get itemsWrapperClass() {
    return this.angularConfig?.itemsWrapperClass ?? '';
  }

  private initChildWrapperInlineStyle() {
    const { isMobile, isDesktop, isMidsize } = this.viewportService;
    this.childWrapperInlineStyle = this.concatInlineStyles.transform([
      this.angularConfig?.childWrapperInlineStyle,
      isMobile && this.angularConfig?.mobileChildWrapperInlineStyle,
      isMidsize && this.angularConfig?.midsizeChildWrapperInlineStyle,
      isDesktop && this.angularConfig?.desktopChildWrapperInlineStyle,
    ]);
  }

  private initChildWrapperClasses() {
    const cls: Array<string | null | undefined | false> = [
      'flex-grid',
      'flexbox-container',
    ];

    if (this.flexDirection) {
      cls.push(
        String(this.flexDirection.flexDirectionClass),
        'flex-' + this.flexDirection.flexDirectionClass
      );
      cls.push('flex-nowrap');
    } else {
      cls.push('flex-wrap');
    }
    if (this.justifyContent) {
      cls.push(
        String(this.justifyContent.justificationClass),
        'flex-' + this.justifyContent?.justificationClass
      );
    }

    if (this.angularConfig) {
      const {
        midsizeClass,
        mobileClass,
        desktopClass,
        flexboxDisplay,
        isCarousel,
      } = this.angularConfig;
      const { isDesktop, isMidsize, isMobile } = this.viewportService;

      cls.push(
        flexboxDisplay && 'd-block',
        isDesktop && desktopClass,
        isMidsize && midsizeClass,
        isMobile && mobileClass,
        isCarousel &&
          `carousel-cards-container total-cards-${
            this.nestedFlexboxItem?.length ?? 0
          }`
      );
    }

    cls.push(
      this.flexCenter && 'flex-center',
      this.flexVcenter && 'flex-vcenter'
    );

    if (this.angularConfig?.childWrapperClass) {
      if (this.angularConfig.childWrapperClass.includes('row')) {
        const index = cls.findIndex((e) => e === 'flexbox-container');
        cls.splice(index, 1);
      }
      cls.push(this.angularConfig.childWrapperClass);
    }

    this.childWrapperClasses = cls.filter((c) => c).join(' ');
  }

  private initBackgroundClasses() {
    const cls = ['flexbox-container-bg'];

    if (this.cmsImage) {
      cls.push(`resp-img-${this.revisionId}`);
      cls.push('full-bg');
    }

    if (this.fullWidthBackground) {
      cls.push('fw-bg');
      cls.push('main-block-flex');
    }

    this.backgroundClasses = cls.join(' ');
  }
}
